/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { CustomerInformationCardProps } from '@/account/types';
import { Card, Button } from '@adobe-commerce/elsie/components';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/account/components/CustomerInformationCard/CustomerInformationCard.css';

export const CustomerInformationCard: FunctionComponent<
  CustomerInformationCardProps
> = ({
  createdAt,
  slots,
  orderedCustomerData,
  showEditForm,
  showChangePassword,
  handleShowChangePassword,
  handleShowEditForm,
}) => {
  const translations = useText({
    buttonSecondary:
      'Account.minifiedView.CustomerInformation.customerInformationCard.buttonSecondary',
    buttonPrimary:
      'Account.minifiedView.CustomerInformation.customerInformationCard.buttonPrimary',
    accountCreation:
      'Account.minifiedView.CustomerInformation.customerInformationCard.accountCreation',
  });

  return (
    <Card
      variant="secondary"
      className={classes([
        'account-customer-information-card',
        [
          'account-customer-information-card-short',
          showChangePassword || showEditForm,
        ],
      ])}
    >
      <div className="account-customer-information-card__wrapper">
        <div className="account-customer-information-card__actions">
          <Button
            // @ts-ignore
            type="button"
            variant="tertiary"
            onClick={handleShowChangePassword}
          >
            {translations.buttonSecondary}
          </Button>

          <Button
            // @ts-ignore
            type="button"
            variant="tertiary"
            onClick={handleShowEditForm}
          >
            {translations.buttonPrimary}
          </Button>
        </div>
        <div className="account-customer-information-card__content">
          {slots?.CustomerData ? (
            <Slot
              name="CustomerData"
              slot={slots?.CustomerData}
              context={{ customerData: orderedCustomerData }}
            />
          ) : (
            <>
              {orderedCustomerData?.map((el, index) => {
                const value = !el?.label
                  ? el?.value
                  : `${el.label}: ${el?.value}`;

                return (
                  <p
                    key={`${el.name}_${index}`}
                    data-testid={`${el.name}_${index}`}
                  >
                    {value}
                  </p>
                );
              })}
              <p>
                {translations.accountCreation}: {createdAt}
              </p>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
